// src/components/inbox/InboxList.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Loader from "../Loader";
import { Button, cn } from "../ui";
import { openThreadForClient } from "../../shared/_services/inbox.services";
import {
  filterChanged,
  loadInbox,
  selectInboxCounts,
  selectInboxError,
  selectInboxFilter,
  selectInboxRows,
  selectInboxStatus,
  selectNoMessagesYet,
  selectPinnedRow,
} from "../../redux/inboxSlice";
import InboxRow from "./InboxRow";
import LoadFailed from "./LoadFailed";
import {
  CHIP_ALL,
  CHIP_MINE,
  EMPTY_LIST,
  INBOX_SUBTITLE,
  INBOX_TITLE,
  LIST_FOOTER,
  LOAD_FAILED,
  NO_MESSAGES_GROUP,
  waitingChip,
} from "./inboxFormat";

// The list column: header, chips, the server-ordered rows, the "No messages
// yet" group and the footer. Opening a client with no thread creates the
// thread first (POST /clients/:clientId/thread), refreshes the list so the
// new thread is in it (InboxPage's deep-link check, R70), then navigates.
export default function InboxList({ selectedThreadId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const filter = useSelector(selectInboxFilter);
  const rows = useSelector(selectInboxRows);
  const noMessagesYet = useSelector(selectNoMessagesYet);
  const counts = useSelector(selectInboxCounts);
  const status = useSelector(selectInboxStatus);
  const error = useSelector(selectInboxError);
  const pinned = useSelector((s) => selectPinnedRow(s, selectedThreadId));
  const [creatingFor, setCreatingFor] = useState(null);
  // The selected conversation stays in view whatever the chip (R77): a
  // refetch that no longer lists it (answered, taken over) keeps it where
  // it last sat.
  const shown = pinned
    ? [...rows.slice(0, pinned.index), pinned.row, ...rows.slice(pinned.index)]
    : rows;

  const open = async (row) => {
    if (row.threadId) {
      navigate(`/inbox/${row.threadId}`);
      return;
    }
    if (creatingFor) return;
    setCreatingFor(row.clientId);
    try {
      const res = await openThreadForClient(row.clientId);
      await dispatch(loadInbox({ quiet: true }));
      navigate(`/inbox/${res.data.thread._id}`);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || LOAD_FAILED, {
        variant: "error",
      });
    } finally {
      setCreatingFor(null);
    }
  };

  const chip = (id, label) => (
    <Button
      key={id}
      variant={filter === id ? "primary" : "quiet"}
      size="sm"
      aria-pressed={filter === id}
      onClick={() => dispatch(filterChanged(id))}
    >
      {label}
    </Button>
  );
  const loading = status === "idle" || status === "loading";

  return (
    <aside
      data-slot="inbox-col--list"
      className={cn("tw-flex tw-min-h-0 tw-w-80 tw-flex-none tw-flex-col tw-border-0 tw-border-r tw-border-solid tw-border-divider tw-bg-surface max-rail:tw-w-full max-rail:tw-border-b max-rail:tw-border-r-0",
        selectedThreadId && "max-rail:tw-max-h-96",
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-p-4">
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
          <h2 className="tw-m-0 tw-text-xl tw-font-bold tw-text-text">
            {INBOX_TITLE}
          </h2>
          <span className="tw-text-xs tw-text-text-muted">
            {INBOX_SUBTITLE}
          </span>
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {chip("waiting", waitingChip(counts?.waiting))}
          {chip("mine", CHIP_MINE)}
          {chip("all", CHIP_ALL)}
        </div>
      </div>
      <div className="tw-min-h-0 tw-flex-1 tw-divide-y tw-divide-solid tw-divide-divider tw-overflow-y-auto">
        {loading && <Loader />}
        {status === "error" && (
          <LoadFailed message={error} onRetry={() => dispatch(loadInbox())} />
        )}
        {status === "ready" && (
          <>
            {shown.length === 0 && <p className="tw-m-0 tw-p-4 tw-text-sm tw-text-text-muted">
                {EMPTY_LIST}
              </p>}
            {shown.map((row) => (
              <InboxRow
                key={row.threadId}
                row={row}
                selected={row.threadId === selectedThreadId}
                onOpen={open}
              />
            ))}
            {noMessagesYet.length > 0 && (
              <>
                <div className="tw-bg-surface-sunken tw-px-4 tw-pb-1 tw-pt-3 tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">
                  {NO_MESSAGES_GROUP}
                </div>
                {noMessagesYet.map((row) => (
                  <InboxRow
                    key={row.threadId ?? `client-${row.clientId}`}
                    row={row}
                    noMessages
                    selected={
                      !!row.threadId && row.threadId === selectedThreadId
                    }
                    busy={creatingFor === row.clientId}
                    onOpen={open}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
      <p className="tw-m-0 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-px-4 tw-py-3 tw-text-xs tw-text-text-muted">
        {LIST_FOOTER}
      </p>
    </aside>
  );
}
